import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import getUrl from '../utils/getUrl';
import getAbsUrl from '../utils/getAbsUrl';
import { getMatchesInString } from '../utils/getMatchesInString';
import { techPages } from '../utils/consts';

const Meta = ({
  title,
  description,
  image,
  ogType,
  ogUrl,
  ogSiteName,
  ogLocale,
  ogLocaleAlternate,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageType,
  ogImageWidth,
  ogImageHeight,
  ogImageAlt,
  twitterCard,
  twitterSite,
  twitterCreator,
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonical,
  alternates,
  author,
  publishedTime,
  articleAuthor,
  twitterImageSrc,
}) => {
  const router = useRouter();
  const url = getUrl({ router });

  image ??= `${router.basePath}/images/share.png`;
  ogType ??= 'website';
  ogUrl ??= url;
  ogTitle ??= title;
  ogDescription ??= description;
  ogImage ??= image;
  ogImageWidth ??= 1200;
  ogImageHeight ??= 628;
  canonical ??= url;
  articleAuthor ??= author;
  twitterImage ??= image;
  twitterTitle ??= title;
  twitterDescription ??= description;
  twitterImageSrc ??= image;
  publishedTime = new Date(publishedTime);

  publishedTime = !isNaN(publishedTime.getTime()) ? publishedTime.toISOString() : null;

  alternates ??= router.locales.reduce((alternates, locale) => {
    const href = getUrl({ router, locale });

    if (locale === router.defaultLocale) {
      alternates.push({ hreflang: 'x-default', href });
    }

    alternates.push({ hreflang: locale, href });

    return alternates;
  }, []);

  return (
    <Head>
      {title && (
        <title>{title}</title>
      )}
      {author && (
        <meta name="author" content={author} key="author" />
      )}
      {articleAuthor && (
        <meta property="article:author" content={author} key="article:author" />
      )}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime}/>
      )}
      {description && (
        <meta name="description" content={description} key="description" />
      )}
      {ogLocale && (
        <meta property="og:locale" content={ogLocale} key="og:locale" />
      )}
      {ogLocaleAlternate && ogLocaleAlternate.map((locale) => (
        <meta property="og:locale:alternate" content={locale} key={`og:locale:alternate:${locale}`} />
      ))}
      {ogType && (
        <meta property="og:type" content={ogType} key="og:type" />
      )}
      {ogTitle && (
        <meta property="og:title" content={ogTitle} key="og:title" />
      )}
      {ogUrl && (
        <meta property="og:url" content={getAbsUrl(ogUrl)} key="og:url" />
      )}
      {ogImage && (
        <meta property="og:image" content={getAbsUrl(ogImage)} key="og:image" />
      )}
      {ogImageWidth && (
        <meta property="og:image:width" content={ogImageWidth} key="og:image:width" />
      )}
      {ogImageHeight && (
        <meta property="og:image:height" content={ogImageHeight} key="og:image:height" />
      )}
      {ogImageAlt && (
        <meta property="og:image:alt" content={ogImageAlt} key="og:image:alt" />
      )}
      {ogDescription && (
        <meta property="og:description" content={ogDescription} key="og:description" />
      )}
      {ogSiteName && (
        <meta property="og:site_name" content={ogSiteName}/>
      )}
      {ogImageType && (
        <meta property="og:image:type" content={ogImageType} key="og:image:type" />
      )}
      {twitterCard && (
        <meta name="twitter:card" content={twitterCard} key="twitter:card" />
      )}
      {twitterSite && (
        <meta name="twitter:site" content={twitterSite} key="twitter:site" />
      )}
      {twitterTitle && (
        <meta name="twitter:title" content={twitterTitle} key="twitter:title" />
      )}
      {twitterCreator && (
        <meta name="twitter:creator" content={twitterCreator} key="twitter:creator" />
      )}
      {twitterDescription && (
        <meta name="twitter:description" content={twitterDescription} key="twitter:description" />
      )}
      {twitterImage && (
        <meta name="twitter:image" content={getAbsUrl(twitterImage)} key="twitter:image" />
      )}
      {twitterImageSrc && (
        <meta name="twitter:image:src" content={getAbsUrl(twitterImageSrc)} key="twitter:image:src" />
      )}
      {canonical && (
        <link rel="canonical" href={getAbsUrl(canonical)} key="canonical" />
      )}
      {alternates && alternates.map(
        ({ hreflang, href }) => !getMatchesInString(href, techPages) &&
          (
            <link
              rel="alternate"
              hrefLang={hreflang}
              href={getAbsUrl(href)}
              key={`alternate:${hreflang}`}
            />
          )
      )}
    </Head>
  );
};

Meta.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  ogType: PropTypes.string,
  ogUrl: PropTypes.string,
  ogLocale: PropTypes.string,
  ogLocaleAlternate: PropTypes.arrayOf(PropTypes.string),
  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.string,
  ogImageType: PropTypes.string,
  ogImageWidth: PropTypes.number,
  ogImageHeight: PropTypes.number,
  ogImageAlt: PropTypes.string,
  ogSiteName: PropTypes.string,
  twitterCard: PropTypes.string,
  twitterSite: PropTypes.string,
  twitterCreator: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterDescription: PropTypes.string,
  twitterImage: PropTypes.string,
  canonical: PropTypes.string,
  author: PropTypes.string,
  publishedTime: PropTypes.string,
  articleAuthor: PropTypes.string,
  twitterImageSrc: PropTypes.string,
  alternates: PropTypes.arrayOf(PropTypes.shape({
    hreflang: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })),
};

export default Meta;
